<template>
  <section class="w-full lg:px-4 px-2.5">
    <div class="flex flex-col lg:flex-col-reverse">
      <div
        class="flex mb-2 lg:mb-8 py-2 border-b flex-col lg:flex-row lg:items-center w-full"
      >
        <div class="hidden lg:flex items-center mb-4 lg:mb-0 overflow-x-auto">
          <button
            v-for="(category, index) in transactionCategories"
            :key="index"
            @click="setCategoryStatus(category)"
          >
            <div
              :class="[
                category.isActive
                  ? 'border border-primary'
                  : ' border-main hover-animation-accent-subtle'
              ]"
              class="text-fg-muted font-normal text-13px mr-1 flex py-1 px-2 items-center rounded-lg whitespace-nowrap"
              v-if="category.isDisplayed"
            >
              <p
                class="mr-1 py-1 capitalize"
                :class="{ 'text-primary': category.isActive }"
              >
                {{ category.name }}
              </p>
              <p
                v-if="!isLoading"
                :class="[
                  category.isActive
                    ? 'text-primary bg-secondary-2'
                    : 'bg-gray-30'
                ]"
                class="py-1 px-2.5 rounded-2xl"
              >
                {{ category.count || 0 }}
              </p>
            </div>
          </button>
        </div>
        <!-- Dropdown to filter transactions by currency -->
        <div class="flex lg:ml-auto">
          <div class="lg:hidden rounded-lg border py-0.5">
            <transaction-category-dropdown />
          </div>
          <div class="flex gap-x-3 items-center ml-auto">
            <div v-if="tenantPermission('multiple-currency-deposit-access')">
              <div class="relative w-full flex items-center justify-end">
                <div>
                  <button
                    @click="toggleDropdown"
                    class="flex items-center pl-2 pr-3 md:pl-4 md:pr-5 py-2 lg:py-1.5 rounded-lg border"
                  >
                    <currency-icon
                      :currency="getCurrencyCountry(filter.currency)"
                    />
                    <p class="hidden xl:block mx-1.5 text-13px font-normal">
                      {{ filter.currency || 'Currency' }}
                    </p>
                    <arrow-down-icon />
                  </button>
                </div>
                <!-- Options for the dropdown -->
                <div
                  v-if="dropdownIsVisible"
                  test-id="dropdown"
                  class="absolute top-12 bg-white border rounded-lg shadow-2xl px-2 py-3 h-48 w-full overflow-scroll z-10"
                >
                  <div
                    v-for="(currency, index) in filteredCurrencyOptions"
                    :key="index"
                    class="cursor-pointer"
                  >
                    <div
                      @click="setCurrencyFilter(currency.currency)"
                      :class="{
                        'bg-muted': filter.currency === currency.currency
                      }"
                      class="hover:bg-bg-muted rounded-lg flex items-center mb-2 px-3 py-2 font-normal text-sm"
                    >
                      <currency-icon
                        :currency="getCurrencyCountry(currency.currency)"
                      />
                      <p class="ml-1.5">{{ currency.currency }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="flex items-center gap-x-1 border border-root-3 text-root-2 px-3.5 xl:px-2.5 xl:py-1.5 lg:py-2 py-2.5 rounded-lg cursor-pointer"
              @click="showDialog('showTableFilter')"
            >
              <filter-icon />
              <p
                class="hidden xl:inline-block text-black-2 text-sm px-2 font-normal"
              >
                Filter
              </p>
              <div class="hidden xl:inline-block">
                <arrow-down-icon />
              </div>
            </div>
            <div
              v-if="tenantPermission('multiple-currency-deposit-access')"
              class="flex items-center"
            >
              <tooltip placement="top" :content="refreshToolTipContent">
                <div class="cursor-pointer" @click="refreshTable">
                  <refresh-icon />
                </div>
              </tooltip>
            </div>
            <div
              v-if="isDashboardUse"
              class="flex items-center gap-x-1 border border-root-3 text-fg-muted px-5 py-3 rounded-lg cursor-pointer mb-6 font-normal text-sm"
              @click="goToTransactions"
            >
              See all
            </div>
          </div>
        </div>
      </div>
      <!-- transaction types -->
      <div class="flex lg:items-start items-center my-3 lg:mt-0 lg:mb-3 w-full">
        <div class="border-b flex items-end w-full justify-between">
          <div
            class="flex items-center gap-x-2 lg:gap-x-3 font-light text-xs lg:text-sm"
          >
            <div
              class="py-2 cursor-pointer px-7 transition-all duration-150 ease-linear"
              :class="activeClass($STATUS.ONGOING)"
              @click="setActiveTab($STATUS.ONGOING)"
            >
              Ongoing
            </div>
            <div
              class="py-2 cursor-pointer px-5"
              :class="activeClass($STATUS.HISTORY)"
              @click="setActiveTab($STATUS.HISTORY)"
            >
              History
            </div>
          </div>
          <div>
            <button
              class="border pt-2 pb-2 pl-2 pr-2 rounded-lg mb-1 flex gap-x-1 gap-y-2 items-center"
              @click="openReportDialog"
            >
              <download-icon color="#5D5E68" size="h-5 w-5" class="" />
              <span class="font-light text-sm hidden md:inline">
                Generate Report</span
              >
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- dialogs -->
    <filter-dialog
      :showDialog="showTableFilter"
      @filterParams="searchFilterParams"
      @closeDialog="closeDialog('showTableFilter')"
      @cancel="handleCancel"
    />
    <generate-report-dialog
      v-if="showReportDialog"
      :showReportDialog="showReportDialog"
      @closeReportDialog="closeReportDialog"
    />
    <!-- table -->
    <div>
      <send-money-transactions-table
        v-if="activeTab === $STATUS.ONGOING"
        @handleCurrentChange="handleCurrentChange"
        @sort="sortHandler"
        :sort-direction="sortDirection"
        :tableData="latestTransactions"
        :isLoading="isLoading"
        :page="page"
        transactionType="ongoing"
      />
      <send-money-transactions-table
        v-if="activeTab === $STATUS.HISTORY"
        @handleCurrentChange="handleCurrentChange"
        @sort="sortHandler"
        :sort-direction="sortDirection"
        :tableData="latestTransactions"
        :isLoading="isLoading"
        :page="page"
        transactionType="history"
      />
    </div>
  </section>
</template>

<script>
import tenant from '@/mixins/tenant.js'
import transactions from '@/mixins/transactions.js' // contains getTransactions() and  transactionCategories which is used here
import { GetCurrencyCountry } from '@/utils/helpers'
import applyDateFilter from '@/mixins/app.js'
import notification from '@/mixins/notification.js'
export default {
  mixins: [tenant, transactions, applyDateFilter, notification],
  data () {
    return {
      activeCategory: 'All Transactions',
      refreshToolTipContent: 'Reset filter settings to default',
      dropdownIsVisible: false,
      sortDirection: 'default',
      activeTab: 'ongoing',
      isLoading: false,
      payload: {
        page: 1,
        size: 10,
        status: 'ongoing',
        type: 'transfer',
        from: '',
        currency: '',
        to: '',
        sort_by: 'created_at',
        sort_direction_desc: true
      },
      showSuccessfulPaymentLinkDialog: false,
      showTableFilter: false,
      filter: {
        currency: ''
      },
      showReportDialog: false
    }
  },
  components: {
    CurrencyIcon: () => import('@/assets/icons/CurrencyIcons.vue'),
    ArrowDownIcon: () => import('@/assets/v2/icons/ArrowDownTwo.vue'),
    RefreshIcon: () => import('@/assets/v2/icons/RefreshIcon.vue'),
    FilterDialog: () =>
      import('@/components/v2/transactions/dialogs/FilterDialog.vue'),
    GenerateReportDialog: () =>
      import('@/components/v2/transactions/dialogs/GenerateReportDialog.vue'),
    FilterIcon: () => import('@/assets/v2/icons/FilterIcon.vue'),
    DownloadIcon: () => import('@/assets/v2/icons/DownloadIcon4.vue'),
    SendMoneyTransactionsTable: () =>
      import('@/components/v2/terminal/tables/SendMoneyTransactionsTable.vue'),
    TransactionCategoryDropdown: () =>
      import(
        '@/components/v2/transactions/dropdowns/TransactionCategoryDropdown.vue'
      )
  },
  computed: {
    isDashboardUse () {
      return this.use === 'dashboard'
    },
    latestTransactions () {
      return this.$store?.state.transactions?.transactions?.items || []
    },
    page () {
      return this.$store?.state.transactions?.transactions?.page || {}
    }
  },
  methods: {
    refreshTable () {
      // reset all filters
      this.payload = {
        page: 1,
        size: 10,
        status: this.activeTab,
        type: 'transfer',
        from: '',
        to: '',
        sort_by: 'created_at',
        sort_direction_desc: true,
        currency: '',
        payment_method: ''
      }
      this.filter = {
        currency: 'Currency'
      }
      this.activeCategory = 'All Transactions'
      this.getTransactions()
    },
    toggleDropdown () {
      this.dropdownIsVisible = !this.dropdownIsVisible
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    setCurrencyFilter (currency) {
      this.filter.currency = currency
      this.dropdownIsVisible = false
      this.handleCurrency()
    },
    async handleCurrency () {
      this.payload.currency = this.filter.currency
      await this.getTransactions()
    },
    sortHandler (sortObj) {
      if (sortObj.sortFormat === 'time') {
        this.$store.dispatch('transactions/sortTimeHandler', sortObj.direction)
      } else if (sortObj.sortFormat === 'amount') {
        this.$store.dispatch(
          'transactions/sortAmountHandler',
          sortObj.direction
        )
      }
    },
    showDialog (dialog) {
      this[dialog] = true
    },
    searchFilterParams (filterParams) {
      this.applyDateFilter(filterParams)
      this.payload.status = this.activeTab
      this.getTransactions()
    },
    closeDialog (dialog) {
      this[dialog] = false
    },
    handleCancel () {
      this.tableFormFilter.startDate = ''
      this.$emit('cancel')
      this.handleClose()
    },
    activeClass (tab) {
      return this.activeTab === tab ? 'active-class' : ''
    },
    async setActiveTab (tab) {
      this.activeTab = tab
      this.payload.status = tab
      this.payload.page = 1
      await this.getTransactions()
    },
    handleCurrentChange (payload) {
      this.payload.page = payload.val
      this.payload.status = payload.transactionType
      this.getTransactions()
    },
    handleOpenFromNotification () {
      if (this.isNotificationRoute) {
        this.setActiveTab(this.updatedNotificationStatus)
        this.getTransactions()
      }
    },
    openReportDialog () {
      this.showReportDialog = true
    },
    closeReportDialog () {
      this.showReportDialog = false
    }
  },
  watch: {
    '$route.params.type': {
      handler: function (newValue) {
        if (newValue) {
          this.handleOpenFromNotification()
        }
      },
      immediate: true
    }
  },
  async mounted () {
    this.handleOpenFromNotification()
    await this.getTransactions()
  }
}
</script>

<style lang="scss" scoped>
.bg {
  background: #ebf2ff;
}
.active-class {
  border-bottom: 1.5px solid var(--color-primary);
  font-weight: 600;
  @apply text-primary;
}
</style>
